import { X } from "lucide-react";
import { useEffect, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-breakpoint";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}

export function Modal({ open, onClose, title, description, children, footer, className }: ModalProps) {
  const isMobile = useIsMobile();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      className={cn(
        "fixed inset-0 z-50 flex animate-ds-fade-in bg-foreground/55",
        isMobile ? "items-end justify-stretch" : "items-center justify-center p-4"
      )}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={cn(
          "bg-card overflow-hidden",
          isMobile
            ? "w-full max-h-[92vh] rounded-t-2xl shadow-modal animate-ds-slide-up"
            : "w-full max-w-[460px] rounded-2xl shadow-modal animate-ds-fade-in",
          className
        )}
      >
        {isMobile && <div className="mx-auto mt-2 mb-1 w-10 h-1 rounded-full bg-foreground/15" />}
        <div className="flex items-start justify-between px-6 pt-6 max-md:px-5">
          <div>
            <div className="text-[17px] font-medium text-foreground font-body">{title}</div>
            {description && <div className="mt-0.5 text-[13px] font-light text-foreground/70 font-body">{description}</div>}
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-foreground/70 hover:bg-foreground/10 transition-colors"
            aria-label="Close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="px-6 py-5 flex flex-col gap-3.5 max-md:px-5 max-h-[60vh] overflow-y-auto">{children}</div>
        {footer && <div className="px-6 pb-6 pt-4 flex justify-end gap-2.5 border-t border-foreground/[0.07] max-md:px-5">{footer}</div>}
      </div>
    </div>
  );
}
